import React, { Component } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
	faPlay,
	faPause,
	faFastForward,
	faVolumeUp,
	faVolumeDown,
	faThumbsUp,
	faThumbsDown,
	faBars
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Slider, Input, Select } from 'antd'

import * as Utils from '../../../components/Utils'
import * as AudioPlayer from '../../../components/AudioPlayer'
import TextInputModal from '../../../components/TextInputModal'
import SeekSlider from './SeekSlider'

import blankCover from '../../../img/blankCover.png'

const NEGATIVE_REASONS = [
	'OUT_OF_BRIEF',
	'ENERGY',
	'MOOD_EMOTION',
	'INSTRUMENTATION',
	'VOCAL',
	'RYTHM',
	'OTHER'
]

const IS_SAFARI =
	typeof navigator !== 'undefined' &&
	/^((?!chrome|android).)*safari/i.test(navigator.userAgent)

class IPlayer extends Component {
	state = {
		reasonSelectorOpened: undefined
	}

	constructor(props) {
		super(props)
		this.commentRef = React.createRef()
		this.bgRef = React.createRef()
	}

	thumbAction = (rating, previousRating) => {
		// check if the the user is canceling a rating
		const userIsCancelingRating = rating === previousRating

		if (!userIsCancelingRating) {
			this.props.setFeedback(
				rating,
				undefined,
				// if rating 1 ( negative ) the value undefined will keep the reason untouched
				// otherwise, null value will remove it
				rating === 1 ? undefined : null
			)

			// for negative feedback we open the reason selector
			if (rating === 1) {
				this.setState({ reasonSelectorOpened: true })
			}
		} else {
			// we also reset the reason but keep the comment
			this.props.setFeedback(rating, undefined, null)
		}
	}

	reasonAction = (reason) => {
		this.setState({ reasonSelectorOpened: undefined })
		this.props.setFeedback(undefined, undefined, reason)

		if (reason === 'OTHER') {
			setTimeout(() => this.commentRef.current?.focus(), 0)
		}
	}

	// This handler is used on safari to force a refresh of the blurred background when the artwork change
	// otherwise we can stay with some artifacts. ENG-2964
	handleArtworkLoaded = () => {
		if (!IS_SAFARI) return

		const el = this.bgRef.current
		if (!el) return
		// Nudge the transform to force Safari to invalidate the cached blur layer
		requestAnimationFrame(() => {
			const currentTransform = el.style.transform
			el.style.transform = 'scale(1.500001)'
			requestAnimationFrame(() => {
				el.style.transform = currentTransform
			})
		})
	}

	render() {
		const {
			track,
			next,
			playPause,
			feedback,
			intl,
			setFeedback,
			playing,
			review,
			mobile,
			showList,
			sample
		} = this.props
		const styles = mobile ? mobileStyles : desktopStyles

		if (!track) {
			return (
				<div style={{ ...styles.container, ...styles.centerContainer }}>
					<div
						className="btn-initial-play"
						style={styles.control}
						onClick={playPause}
					>
						<FontAwesomeIcon
							icon={faPlay}
							size="4x"
							style={{
								color: 'white',
								filter: 'drop-shadow(2px 2px 5px black)'
							}}
						/>
					</div>
				</div>
			)
		}

		const art = Utils.trackArtworkUrl(track)
		return (
			<div style={styles.container}>
				<div
					ref={this.bgRef}
					style={{ ...styles.bg, backgroundImage: `url(${art})` }}
				/>
				<div style={styles.trackInfoContainer}>
					<img
						style={styles.artwork}
						alt=""
						src={art}
						onLoad={this.handleArtworkLoaded}
					/>
					<div style={styles.flex1}>
						<div style={styles.trackInfoText}>
							<h3 style={{ ...styles.info, ...styles.artist }}>
								{track.artist}
							</h3>
							<h2 style={{ ...styles.info, ...styles.title }}>
								{track.title}
							</h2>
						</div>
						<div style={styles.controls}>
							{mobile && (
								<div onClick={showList} style={styles.control}>
									<FontAwesomeIcon icon={faBars} size="2x" />
								</div>
							)}
							<div style={styles.control} onClick={playPause}>
								<FontAwesomeIcon
									icon={playing ? faPause : faPlay}
									size="2x"
								/>
							</div>
							<div style={styles.control} onClick={next}>
								<FontAwesomeIcon
									icon={faFastForward}
									size="2x"
								/>
							</div>
							{!mobile && (
								<div
									style={{
										...styles.slider,
										...styles.control
									}}
								>
									<FontAwesomeIcon icon={faVolumeDown} />
									<Slider
										min={0}
										max={100}
										defaultValue={
											AudioPlayer.targetVolume * 100
										}
										onChange={(v) =>
											AudioPlayer.setTargetVolume(v / 100)
										}
										style={{
											width: 60,
											margin: '0 15px 0 15px'
										}}
									/>
									<FontAwesomeIcon icon={faVolumeUp} />
								</div>
							)}
						</div>
						<div style={styles.seekBarContainer}>
							<SeekSlider
								style={styles.seekBar}
								playing={playing}
								track={track}
							/>
						</div>
					</div>
					{review && (
						<div style={{ ...styles.feedback, ...styles.flex1 }}>
							<h3 style={styles.ratingTitle}>
								<FormattedMessage id="samples.listen.trackFeedback" />
							</h3>
							<div
								style={{
									...styles.ratings,
									display: 'flex',
									gap: '20px',
									flexWrap: 'wrap'
								}}
							>
								<div
									style={{
										marginRight: 20,
										...styles.feedBackButton
									}}
									onClick={() =>
										this.thumbAction(2, feedback?.rating)
									}
								>
									<FontAwesomeIcon
										icon={faThumbsUp}
										size="2x"
										style={{
											color:
												(
													feedback &&
													feedback.rating === 2
												) ?
													'#52c41a'
												:	null,
											marginBottom: 10
										}}
									/>
								</div>
								<div
									style={{ ...styles.feedBackButton }}
									onClick={() =>
										this.thumbAction(1, feedback?.rating)
									}
								>
									<FontAwesomeIcon
										icon={faThumbsDown}
										size="2x"
										style={{
											color:
												(
													feedback &&
													feedback.rating === 1
												) ?
													'#f5222d'
												:	null,
											marginTop: 10
										}}
									/>
								</div>

								{feedback?.rating === 1 && (
									<>
										<style>{`
										.reason-select .ant-select-selector {
											background-color: ${colors.bg} !important;
											border-color: ${colors.subtitle} !important;
											color: ${colors.subtitle} !important;
										}

										.reason-select .ant-select-selection-placeholder,
										.reason-select .ant-select-selection-item {
											color: ${colors.subtitle} !important;
										}

										.reason-select .ant-select-arrow {
											color: ${colors.subtitle};
										}

										/* Dropdown menu */
										.reason-select-dropdown .ant-select-item {
											color: ${colors.subtitle};
											background-color: ${colors.bg};
										}

										.reason-select-dropdown .ant-select-item-option-active {
											background-color: #222;
										}

										.reason-select-dropdown .ant-select-item-option-selected {
											background-color: #333;
										}

										.reason-select-dropdown.ant-select-dropdown {
											background-color: ${colors.bg};
										}
										`}</style>
										<Select
											open={
												this.state.reasonSelectorOpened
											}
											className="reason-select"
											dropdownClassName="reason-select-dropdown" // v4 prop
											style={{
												...styles.reasonSelector,
												flexGrow: 1
											}}
											onChange={(v) =>
												this.reasonAction(v)
											}
											value={feedback?.reason}
											options={NEGATIVE_REASONS.map(
												(r) => {
													return {
														value: r,
														label: intl.formatMessage(
															{
																id: `samples.listen.reasons.${r}`
															}
														)
													}
												}
											)}
										/>
									</>
								)}

								<Input.TextArea
									ref={this.commentRef}
									style={styles.commentInput}
									placeholder={intl.formatMessage({
										id: 'samples.listen.comment'
									})}
									rows={mobile ? 2 : 4}
									onChange={(e) =>
										setFeedback(
											undefined,
											e.target.value,
											undefined
										)
									}
									value={feedback?.comment}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		)
	}
}

export default injectIntl(IPlayer)

const colors = {
	bg: '#181818',
	primary: '#1890ff',
	text: '#fff',
	subtitle: '#999',
	yes: '#52c41a',
	no: '#f5222d'
}

// This filter allow controls to be visible on bright backgrounds
// this can happen when track have a bright artwork
const dropShadow = 'drop-shadow(1px 1px 5px #666)'

const desktopStyles = {
	container: {
		height: 400,
		overflow: 'hidden'
	},
	centerContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	bg: {
		backgroundPosition: 'left',
		backgroundRepeat: 'no-repeat',
		color: '#fff',
		position: 'relative',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		filter: 'blur(40px)',
		transform: 'scale(1.5)'
	},
	trackInfoContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		zIndex: 3,
		display: 'flex',
		overflowX: 'auto'
	},
	artwork: {
		margin: 50,
		width: 300,
		height: 300,
		boxShadow: '0px 10px 20px 0px rgba(0,0,0,0.5)',
		backgroundImage: `url(${blankCover})`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		backgroundRepeat: 'no-repeat',
		border: '1px solid rgba(255, 255, 255, 0.3)',
		objectFit: 'cover'
	},
	trackInfoText: {
		position: 'absolute',
		left: 0,
		right: 20,
		bottom: 150
	},
	flex1: {
		flex: 1,
		position: 'relative',
		minWidth: 280
	},
	info: {
		color: '#fff',
		fontWeight: 700,
		textAlign: 'left',
		textTransform: 'uppercase',
		flex: 1
	},
	artist: {
		fontSize: '16px',
		lineHeight: '20px',
		letterSpacing: '3.2px',
		filter: dropShadow
	},
	title: {
		fontSize: '26px',
		lineHeight: '32px',
		letterSpacing: '4.8px',
		filter: dropShadow
	},
	ratingTitle: {
		color: '#999',
		fontWeight: 700,
		textAlign: 'left',
		textTransform: 'uppercase',
		fontSize: '16px',
		lineHeight: '20px',
		letterSpacing: '3.2px'
	},
	controls: {
		position: 'absolute',
		left: 0,
		right: 20,
		bottom: 100,
		display: 'flex',
		alignItems: 'center'
	},
	seekBarContainer: {
		position: 'absolute',
		left: 0,
		right: 20,
		bottom: 50,
		paddingTop: 20
	},
	seekBar: {
		width: '100%',
		filter: dropShadow
	},
	slider: {
		display: 'flex',
		alignItems: 'center'
	},
	feedBackButton: {
		// no dropshadow in desktop for feedback buttons
	},
	control: {
		marginRight: 40,
		cursor: 'pointer',
		filter: dropShadow
	},
	feedback: {
		padding: '0 50px',
		margin: 'auto 0 50px 0'
	},
	ratings: {
		display: 'flex',
		padding: '20px 0'
	},
	reasonSelector: {
		flexGrow: 1
	},
	commentInput: {
		color: colors.subtitle,
		background: colors.bg,
		borderColor: colors.subtitle
	}
}

const mobileStyles = {
	container: {
		width: '100vw',
		height: '100%',
		overflow: 'hidden',
		position: 'relative'
	},
	centerContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	bg: {
		backgroundPosition: 'center',
		backgroundRepeat: 'no-repeat',
		color: '#fff',
		paddingBottom: 16,
		paddingTop: 16,
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		filter: 'blur(40px)',
		transform: 'scale(1.5)'
	},
	trackInfoContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		zIndex: 3,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		overflowX: 'auto'
	},
	artwork: {
		margin: '30px auto',
		width: '30vh',
		height: '30vh',
		boxShadow: '0px 10px 20px 0px rgba(0,0,0,0.5)',
		background: '#000',
		border: '1px solid rgba(255, 255, 255, 0.3)',
		objectFit: 'cover'
	},
	trackInfoText: {
		padding: '10px 20px'
	},
	flex1: {
		position: 'relative'
	},
	info: {
		color: '#fff',
		fontWeight: 700,
		textAlign: 'center',
		textTransform: 'uppercase',
		flex: 1
	},
	artist: {
		fontSize: '16px',
		lineHeight: '20px',
		letterSpacing: '3.2px',
		filter: dropShadow
	},
	title: {
		fontSize: '26px',
		lineHeight: '32px',
		letterSpacing: '4.8px',
		filter: dropShadow
	},
	ratingTitle: {
		display: 'none'
	},
	controls: {
		width: '100%',
		padding: 10,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	seekBarContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '20px 30px'
	},
	seekBar: {
		width: '100%',
		margin: '0 20px 0 20px',
		filter: dropShadow
	},
	feedBackButton: {
		// in mobile, the thumb button can be "inside" the blured track artwork
		// so they need the drop shadow
		filter: dropShadow
	},
	control: {
		margin: '0 20px',
		cursor: 'pointer',
		filter: dropShadow
	},
	feedback: {},
	ratings: {
		width: '100%',
		padding: '10px 30px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	reasonSelector: {
		flexBasis: '400px',
		flexGrow: 1,
		maxWidth: '100%'
	},
	commentInput: {
		color: colors.subtitle,
		background: colors.bg,
		borderColor: colors.subtitle
	}
}
